import { EditableField } from "./editable-field";
import { InstitutionsSelect } from "../pages/my-team/team-information/institution-select";
import { ErrorMessage } from "./error-message";
import { TeamCreate, UpdateTeamDTO } from "../types/team.types";
import { useInstitutions } from "../context/InstitutionsContext";
import { useEffect, useState } from "react";
import { Button } from "./button";
import { EyeIcon, InformationCircleIcon } from "@heroicons/react/24/solid";
import { UsersAPI } from "../APIs/users.api";
import {
  HelpDeskTicket,
  HelpDeskTicketStatus,
} from "../types/help_desk_tickets.types";
import { useNavigate } from "react-router-dom";

interface ITeamFormBaseProps {
  title?: string;
  buttonText: string;
  isSubmitting: boolean;
  handleChange: (e: React.ChangeEvent<HTMLElement>) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel?: () => void;
  onRequestNewInstitution?: () => void;
}

interface ITeamFormEditProps extends ITeamFormBaseProps {
  team: UpdateTeamDTO;
  createTeam?: never;
}

interface ITeamFormCreateProps extends ITeamFormBaseProps {
  team?: never;
  createTeam: TeamCreate;
}

export type ITeamFormProps = ITeamFormEditProps | ITeamFormCreateProps;

export function TeamForm({
  title,
  buttonText,
  team,
  createTeam,
  handleChange,
  onSubmit,
  isSubmitting,
  onCancel,
  onRequestNewInstitution,
}: ITeamFormProps) {
  const { getInstitutions, error } = useInstitutions();
  const [HDTickets, setHDTickets] = useState<HelpDeskTicket[]>([]);
  const currentTeam = team ?? createTeam;
  const navigate = useNavigate();

  const institutionId = team ? team.institution_id : createTeam?.institution_id;

  useEffect(() => {
    async function fetchData() {
      getInstitutions();

      if (onRequestNewInstitution) {
        try {
          const response = await UsersAPI.getUserHDTickets();
          if (response.statusCode === 200) {
            setHDTickets(response.data);
          }
        } catch (error) {
          console.error("Error fetching user HD tickets:", error);
        }
      }
    }

    fetchData();
  }, []);
  const hasOpenHDTicket = HDTickets.some(
    (ticket) => ticket.status === HelpDeskTicketStatus.Pending
  );

  if (!currentTeam) {
    return null;
  }

  return (
    <>
      {title && (
        <h2 className="text-2xl font-bold text-center text-gray-600 mb-4">
          {title}
        </h2>
      )}
      <form onSubmit={onSubmit} className="space-y-4">
        <EditableField
          label="Nombre del Equipo"
          value={currentTeam.name}
          onChange={handleChange}
          required
          type="text"
          name="name"
          minLength={3}
          maxLength={100}
          placeholder="Ingrese el nombre del equipo"
          autocomplete="off"
        />

        <InstitutionsSelect value={institutionId!} onChange={handleChange} />

        {onRequestNewInstitution && !hasOpenHDTicket && (
          <div className="mt-2 text-sm text-yellow-800 bg-yellow-50 p-3 rounded-lg border border-yellow-200">
            <div className="flex items-center mb-2">
              <InformationCircleIcon className="h-6 w-6 text-yellow-500 mr-2" />
              <span className="font-semibold">
                ¿No encontras tu barra pesquera?
              </span>
            </div>
            <p className="mb-2">
              Si tu barra pesquera no aparece en la lista de selección, podes
              solicitar que sea añadida al sistema.
            </p>
            <button
              type="button"
              onClick={onRequestNewInstitution}
              className="w-full bg-yellow-100 text-yellow-800 py-2 rounded-lg hover:bg-yellow-200 transition-colors font-semibold"
            >
              Solicitar Alta de Barra Pesquera
            </button>
          </div>
        )}

        {hasOpenHDTicket && (
          <div className="mt-2 text-sm text-yellow-800 bg-yellow-50 p-3 rounded-lg border border-yellow-200">
            <div className="flex items-center mb-2">
              <InformationCircleIcon className="h-6 w-6 text-yellow-500 mr-2" />
              <span className="font-semibold">Solicitud en Proceso</span>
            </div>
            <p className="mb-2">
              Actualmente tienes una solicitud pendiente para añadir una nueva
              barra pesquera al sistema.
            </p>
            <p className="mb-2">
              Mientras tu solicitud está siendo revisada, no puedes:
            </p>
            <ul className="list-disc list-inside pl-2">
              <li>Crear tu equipo</li>
              <li>Editar tu equipo</li>
              <li>Solicitar otra barra pesquera</li>
            </ul>

            <div className="mt-4">
              <button
                onClick={() => navigate("/dashboard/help")}
                type="button"
                className="w-full bg-yellow-100 text-yellow-800 py-2 rounded-lg hover:bg-yellow-200 transition-colors flex items-center justify-center font-semibold"
              >
                Ver Mis Solicitudes
                <EyeIcon className="ml-2 h-5 w-5" />
              </button>
            </div>

            <p className="mt-2 text-sm text-yellow-600">
              Por favor, espera a la resolución de tu solicitud actual para
              poder crear o editar tu equipo.
            </p>
          </div>
        )}

        {error && <ErrorMessage message={error} />}

        <div className="flex justify-end pt-2">
          {onCancel && (
            <Button
              type="button"
              text="Cancelar"
              variant="cancel"
              onClick={onCancel}
            />
          )}
          <Button
            type="submit"
            text={buttonText}
            variant="create"
            className="ml-2"
            disabled={isSubmitting || hasOpenHDTicket}
          />
        </div>
      </form>
    </>
  );
}
